import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet, asOverrideKey } from "@/lib/api-client";
import { policyKeys } from "@/lib/query";
import { AgencyDetailSchema } from "@/schemas/agent";

// Date fields wire-serialize to ISO strings via Response.json. Some legacy
// queries also return raw Date instances when the row passes through Drizzle
// untouched; nullable strings cover both shapes once they reach the client.
const dateString = z.string().nullable();

const PolicyDetailSchema = z.object({
  policyNumber: z.string(),
  effectiveDate: dateString,
  expirationDate: dateString,
  inceptionDate: dateString,
  insuredName: z.string().nullable(),
  ncciStateNumber: z.string().nullable(),
  cancellationDate: dateString,
  cancelDescription: z.string().nullable(),
  policyStatus: z.string().nullable(),
  ratingTier: z.string().nullable(),
  liabilityLimits: z.string().nullable(),
  paymentPlan: z.string().nullable(),
  subrogationWaiver: z.string().nullable(),
  exclusionWaiver: z.string().nullable(),
  noEmployees: z.number().nullable(),
  governingClassCode: z.string().nullable(),
  governingClassDescription: z.string().nullable(),
  insuredType: z.string().nullable(),
  businessDescription: z.string().nullable(),
  naicsCode: z.string().nullable(),
  emod: z.number().nullable(),
  deductibleType: z.string().nullable(),
  deductibleTypeLabel: z.string().nullable(),
  deductibleLimit: z.string().nullable(),
  deductibleAggregate: z.string().nullable(),
  epliCoverage: z.string().nullable(),
  feinTaxId: z.string().nullable(),
  policyDividendPaid: z.number().nullable(),
});

const PolicyPeriodsSchema = z.array(
  z.object({
    effectiveDate: z.string(),
    expirationDate: z.string(),
    policyStatus: z.string().nullable(),
  }),
);

const PolicyAgencySchema = AgencyDetailSchema.nullable();

const PolicyStatesSchema = z.array(
  z.object({
    id: z.string(),
    stateCode: z.string(),
    stateName: z.string().nullable(),
    scheduleRating: z.string().nullable(),
    writtenPremium: z.number(),
  }),
);

const LossRatioPremiumSchema = z.object({
  effectiveDate: z.string(),
  expirationDate: z.string().nullable(),
  writtenPremium: z.number(),
  earnedPremium: z.number(),
  incurredLoss: z.number(),
  writtenLossRatio: z.number(),
  earnedLossRatio: z.number(),
});

const LossRatioWindowSchema = z.object({
  startYear: z.number(),
  endYear: z.number(),
  earnedPremium: z.number(),
  calendar: z.object({ incurredLoss: z.number(), lossRatio: z.number() }),
  accident: z.object({ incurredLoss: z.number(), lossRatio: z.number() }),
});

const PolicyLossRatioSchema = z.object({
  premiums: z.array(LossRatioPremiumSchema),
  totalWritten: z.number(),
  totalEarned: z.number(),
  totalIncurred: z.number(),
  lossRatio: z.number(),
  writtenLossRatio: z.number(),
  earnedLossRatio: z.number(),
  policyYear: z.object({
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    earnedPremium: z.number(),
    incurredLoss: z.number(),
    lossRatio: z.number(),
  }),
  anchorYear: z.number(),
  currentYear: LossRatioWindowSchema,
  threeYear: LossRatioWindowSchema,
  fiveYear: LossRatioWindowSchema,
});

const ClaimBucketSchema = z.object({
  total: z.number(),
  openIndemnity: z.number(),
  openMedicalOnly: z.number(),
});

const PolicyClaimCountsSchema = z.object({
  insuredAddress: z.string().nullable(),
  total: z.number(),
  open: z.number(),
  openIndemnity: z.number(),
  openMedicalOnly: z.number(),
  financials: z.object({
    reserve: ClaimBucketSchema,
    paid: ClaimBucketSchema,
    recovered: ClaimBucketSchema,
  }),
});

export type PolicyDetail = z.infer<typeof PolicyDetailSchema>;
export type PolicyLossRatioData = z.infer<typeof PolicyLossRatioSchema>;
export type LossRatioPremiumRow = z.infer<typeof LossRatioPremiumSchema>;
export type LossRatioWindow = z.infer<typeof LossRatioWindowSchema>;
export type PolicyClaimCounts = z.infer<typeof PolicyClaimCountsSchema>;

// Build `/api/policies/:n[/suffix][?effectiveDate=...]` for the detail hooks
// below. Every term-scoped policy endpoint takes the same optional
// `effectiveDate` query param, so do the encoding in one place.
function policyUrl(policyNumber: string, suffix?: string, effectiveDate?: string) {
  const base = `/api/policies/${encodeURIComponent(policyNumber)}${suffix ? `/${suffix}` : ""}`;
  return effectiveDate ? `${base}?effectiveDate=${encodeURIComponent(effectiveDate)}` : base;
}

// `?as=<sam>` impersonation flips the effective identity for restriction
// checks at the API layer. Every policy hook below pins its cache to the
// override so switching identities mid-session does not serve the prior
// identity's restricted data — see use-claim.ts for the same pattern.
export function usePolicy(policyNumber: string, effectiveDate?: string) {
  return useQuery({
    queryKey: [...policyKeys.detail(policyNumber, effectiveDate), asOverrideKey()] as const,
    queryFn: () => apiGet(policyUrl(policyNumber, undefined, effectiveDate), PolicyDetailSchema),
  });
}

export function usePolicyPeriods(policyNumber: string) {
  return useQuery({
    queryKey: [...policyKeys.periods(policyNumber), asOverrideKey()] as const,
    queryFn: () => apiGet(policyUrl(policyNumber, "periods"), PolicyPeriodsSchema),
  });
}

export function usePolicyAgency(policyNumber: string) {
  return useQuery({
    queryKey: [...policyKeys.agency(policyNumber), asOverrideKey()] as const,
    queryFn: () => apiGet(policyUrl(policyNumber, "agency"), PolicyAgencySchema),
  });
}

export function usePolicyLossRatio(policyNumber: string, effectiveDate?: string) {
  return useQuery({
    queryKey: [...policyKeys.lossRatio(policyNumber, effectiveDate), asOverrideKey()] as const,
    queryFn: () =>
      apiGet(policyUrl(policyNumber, "loss-ratio", effectiveDate), PolicyLossRatioSchema),
  });
}

export function usePolicyStates(policyNumber: string, effectiveDate?: string) {
  return useQuery({
    queryKey: [...policyKeys.states(policyNumber, effectiveDate), asOverrideKey()] as const,
    queryFn: () => apiGet(policyUrl(policyNumber, "states", effectiveDate), PolicyStatesSchema),
  });
}

export function usePolicyClaimCounts(policyNumber: string) {
  return useQuery({
    queryKey: [...policyKeys.claimCounts(policyNumber), asOverrideKey()] as const,
    queryFn: () => apiGet(policyUrl(policyNumber, "claim-counts"), PolicyClaimCountsSchema),
    enabled: policyNumber.length > 0,
  });
}
